import { store } from "@store/index";
import { trpc } from "@utils/api";
import { buildListHref } from "@schemas/pages/helpers/pagePaths";
import {
  type PageV2Component,
  type SideNavigationV2Props,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { open as openDialog } from "@store/features/layout/dialogSlice";
import {
  close as closeSideNavigation,
  updateParams as updateSideParams,
} from "@store/features/layout/sideNavigationSlice";
import type { AssociationRule } from "skedyul-core/features/crm/features/imports/resources/types";

type AssociationRuleRow = {
  id: string;
  relation: string;
  parentModel: string;
  parentField: string;
  strategy: string;
  existing: string;
};

type ImportParams = {
  subdomain: string;
  fileId: string;
  modelId: string;
  modelName: string;
  modelNamePlural: string;
  modelHandlePlural?: string;
  listPagePath?: string;
  totalRecords: number;
  uniqueFields: Array<{ id: string; label: string; handle: string }>;
  importId: string;
  importMode?: string;
  matchOnFieldId?: string;
  associationCandidates?: Array<{
    relationField: { id: string; label: string; handle: string };
    parentModel: { id: string; label: string; handle: string };
    parentUniqueFields: Array<{
      id: string;
      label: string;
      handle: string;
      csvPathSuffix?: string;
    }>;
  }>;
  associationRules?: AssociationRule[];
};

export const importCSVSidebarSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }> & {
    props: ImportParams;
  },
): SideNavigationV2Props<PageV2Component> => {
  const state = args.state.sideNavigation.right;
  const p = {
    ...(state.params as ImportParams),
    ...(args.props || {}),
  } as ImportParams;

  const importMode = p.importMode || "add-or-update";
  const [firstUniqueField] = p.uniqueFields || [];
  const matchOnFieldId = p.matchOnFieldId || firstUniqueField?.id || "";
  const associationRules = (p.associationRules || []).filter(
    (r): r is AssociationRule =>
      Boolean(r.relationFieldId && r.parentModelId && r.parentFieldId),
  );

  const updateParams = (next: Partial<ImportParams>) => {
    store.dispatch(updateSideParams({ position: "right", params: next }));
  };

  return {
    side: "right",
    isOpen: state.isOpen,
    width: "720px",
    component: {
      component: "PageV2",
      props: {
        hideTitle: true,
        title: `Import ${p.modelNamePlural}`,
        header: {
          icon: "Upload",
          primaryAction: {
            label: "Start Import",
            variant: "default",
            size: "sm",
            isDisabled: !(importMode && matchOnFieldId),
            handleClick: async () => {
              if (!(importMode && matchOnFieldId)) return;
              try {
                const importModeMap = {
                  "add-only": "ADD_NEW_ONLY",
                  "add-or-update": "ADD_OR_UPDATE",
                  "update-only": "UPDATE_EXISTING_ONLY",
                } as const;

                await trpc.crm.updateImport.mutate({
                  importId: p.importId,
                  importMode:
                    importModeMap[importMode as keyof typeof importModeMap],
                  matchOnFieldId,
                  associationRules,
                });

                await trpc.workflows.startImport.mutate({
                  importId: p.importId,
                  throttleMs: 100,
                  maxConcurrency: 5,
                });

                try {
                  const listPath =
                    p.listPagePath ||
                    p.modelHandlePlural ||
                    p.modelNamePlural ||
                    "";
                  if (listPath) {
                    const filters = { import: { eq: p.importId } } as Record<
                      string,
                      unknown
                    >;
                    const search = new URLSearchParams({
                      filters: JSON.stringify(filters),
                    });
                    args.routerContext.router.push(
                      buildListHref(p.subdomain, listPath, search),
                    );
                  }
                } catch {}

                store.dispatch(closeSideNavigation({ position: "right" }));
              } catch (error) {
                console.error("Failed to start import:", error);
              }
            },
          },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Import Properties",
              props: {
                header: {
                  title: "Import Properties",
                  description: "Configure your import settings",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Input" as const,
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text" as const,
                        leftIcon: "File",
                        label: "Total Records Found",
                        value: `${p.totalRecords} records`,
                        id: "total-records",
                        disabled: true,
                      },
                    },
                    {
                      component: "Select" as const,
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        label: "Import Mode",
                        id: "import-mode",
                        value: importMode,
                        leftIcon: "BetweenHorizontalEnd",
                        items: [
                          { label: "Only Add New Records", value: "add-only" },
                          {
                            label: "Add or Update Existing Records",
                            value: "add-or-update",
                          },
                          {
                            label: "Only Update Existing Records",
                            value: "update-only",
                          },
                        ],
                        handleChange: (value: string) =>
                          updateParams({ importMode: value }),
                        helpText:
                          "Choose how to handle existing records. 'Add or Update' will create new records and update existing ones based on the match field.",
                      },
                    },
                    {
                      component: "Select" as const,
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        label: "Match On",
                        id: "match-on",
                        placeholder: "Select a field to match existing records",
                        value: matchOnFieldId,
                        leftIcon: "ScanFace",
                        items: [
                          ...p.uniqueFields.map((f) => ({
                            label: f.label,
                            value: f.id,
                          })),
                        ],
                        handleChange: (value: string) =>
                          updateParams({ matchOnFieldId: value }),
                        helpText:
                          "Choose a unique field (like email or ID) to identify existing records.",
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Associations",
              props: {
                header: {
                  title: "Associations",
                  description:
                    "Configure how to link parents for relation fields",
                },
                secondaryActions:
                  p.associationCandidates && p.associationCandidates.length
                    ? [
                        {
                          label: "Add Association",
                          variant: "outline" as const,
                          size: "sm" as const,
                          leftIcon: "Plus" as const,
                          handleClick: () => {
                            const defaultRel = (p.associationCandidates ||
                              [])[0];
                            const defaultParent =
                              defaultRel?.parentUniqueFields?.[0];
                            store.dispatch(
                              openDialog({
                                schema: "ADD_ASSOCIATION_RULE",
                                params: {
                                  associationCandidates:
                                    p.associationCandidates || [],
                                  associationRules,
                                  selectedRelationFieldId:
                                    defaultRel?.relationField.id,
                                  selectedParentFieldId: defaultParent?.id,
                                  childModelName: p.modelName,
                                },
                              }),
                            );
                          },
                        },
                      ]
                    : [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "DataTable" as const,
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        data: associationRules.map(
                          (r, i): AssociationRuleRow => {
                            const cand = (p.associationCandidates || []).find(
                              (c) => c.relationField.id === r.relationFieldId,
                            );
                            const parentFieldLabel =
                              cand?.parentUniqueFields.find(
                                (pf) => pf.id === r.parentFieldId,
                              )?.label || r.parentFieldId;
                            const strategy = r.rejectIfNotFound
                              ? "Require parent; reject row"
                              : r.autoCreateIfNotFound
                                ? "Create parent if missing"
                                : "Leave unlinked";
                            return {
                              id: String(i),
                              relation:
                                cand?.relationField.label || r.relationFieldId,
                              parentModel:
                                cand?.parentModel.label || r.parentModelId,
                              parentField: parentFieldLabel,
                              strategy,
                              existing: r.updateExistingBehavior || "update",
                            };
                          },
                        ),
                        columns: [
                          {
                            id: "parentModel",
                            type: "rowLayout",
                            header: "Parent Model",
                            accessorKey: "parentModel",
                            align: "left",
                            components: [
                              {
                                component: "Text",
                                generateProps: (row: AssociationRuleRow) => ({
                                  children: row.parentModel,
                                }),
                              },
                            ],
                          },
                          {
                            id: "parentField",
                            type: "rowLayout",
                            header: "Parent Field",
                            accessorKey: "parentField",
                            align: "left",
                            components: [
                              {
                                component: "Text",
                                generateProps: (row: AssociationRuleRow) => ({
                                  children: row.parentField,
                                }),
                              },
                            ],
                          },
                          {
                            id: "strategy",
                            type: "rowLayout",
                            header: "Strategy",
                            accessorKey: "strategy",
                            align: "left",
                            components: [
                              {
                                component: "Text",
                                generateProps: (row: AssociationRuleRow) => ({
                                  children: row.strategy,
                                }),
                              },
                            ],
                          },
                        ],
                        onRowClick: (row: { id: string }) => {
                          const idx = Number(row.id);
                          const rule = associationRules[idx];
                          if (!rule) return;
                          const strategy = rule.rejectIfNotFound
                            ? "associate-reject"
                            : rule.autoCreateIfNotFound
                              ? "associate-create"
                              : "associate-ignore";
                          store.dispatch(
                            openDialog({
                              schema: "ADD_ASSOCIATION_RULE",
                              params: {
                                associationCandidates:
                                  p.associationCandidates || [],
                                associationRules,
                                ruleIndex: idx,
                                selectedRelationFieldId: rule.relationFieldId,
                                selectedParentFieldId: rule.parentFieldId,
                                associationStrategy: strategy,
                                childModelName: p.modelName,
                              },
                            }),
                          );
                        },
                        emptyMessage:
                          "No association rules configured. Add an association to link parents during import.",
                        emptyActions:
                          p.associationCandidates &&
                          p.associationCandidates.length
                            ? [
                                {
                                  label: "Add Association",
                                  variant: "outline" as const,
                                  size: "sm" as const,
                                  leftIcon: "Plus" as const,
                                  handleClick: () => {
                                    const defaultRel =
                                      (p.associationCandidates || [])[0];
                                    const defaultParent =
                                      defaultRel?.parentUniqueFields?.[0];
                                    store.dispatch(
                                      openDialog({
                                        schema: "ADD_ASSOCIATION_RULE",
                                        params: {
                                          associationCandidates:
                                            p.associationCandidates || [],
                                          associationRules,
                                          selectedRelationFieldId:
                                            defaultRel?.relationField.id,
                                          selectedParentFieldId:
                                            defaultParent?.id,
                                          childModelName: p.modelName,
                                        },
                                      }),
                                    );
                                  },
                                },
                              ]
                            : [],
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  };
};
